import { IonItem, IonLabel, IonList, IonPage } from "@ionic/react";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/button";
import { Content } from "@/components/content";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { EditableAvatar } from "@/components/molecules/auth/editable-avatar";
import { OrganizationSwitcher } from "@/components/molecules/organization/organization-switcher";
import { useMe } from "@/lib/hooks/use-me";
import { chevronRightIcon } from "@/lib/icons/chevron-right.icon";
import { useMyOrganizations } from "@/lib/query/organization/use-my-organizations";
import { Routes } from "@/lib/router";
import { cleanupDeviceRegistration } from "@/lib/utils/notifications";
import { authClient } from "@/utils/auth";
import packageJson from "../../../package.json";

export function SettingsPage() {
  const { t } = useTranslation();
  const modalSignOut = useRef<HTMLIonModalElement>(null);
  const modalOrganizationSwitcher = useRef<HTMLIonModalElement>(null);
  const modalDebugInfo = useRef<HTMLIonModalElement>(null);
  const { me, isNotEmployee } = useMe();
  const { data: organizationsData } = useMyOrganizations();
  const [versionClickCount, setVersionClickCount] = useState(0);
  const [debugInfo, setDebugInfo] = useState<Record<string, unknown>>({});

  const organizations = organizationsData?.organizations ?? [];
  const hasMultipleOrganizations = organizations.length > 1;
  const organizationSlug = import.meta.env.VITE_ORGANIZATION_SLUG;
  const shouldShowMultipleOrganizations =
    hasMultipleOrganizations && (!organizationSlug || organizationSlug === "boomer");

  const activeOrganization = organizations.find(
    (org) => org.id === organizationsData?.activeOrganizationId,
  );

  return (
    <IonPage>
      <Content className="part-scroll:gap-6" withTopPadding>
        <div className="flex flex-col items-center gap-3 py-6">
          <EditableAvatar userId={me?.id ?? ""} imageKey={me?.image ?? ""} className="shadow-lg" />

          <div className="flex flex-col items-center gap-1">
            <strong className="text-xl text-txt-primary">{me?.name}</strong>
            <strong className="text-lg text-brand-500">
              {isNotEmployee ? t("common:user.type.user") : t("common:user.type.employee")}
            </strong>
          </div>
        </div>

        <IonList lines="none">
          {shouldShowMultipleOrganizations && (
            <IonItem
              detail
              detailIcon={chevronRightIcon}
              button
              onClick={() => modalOrganizationSwitcher.current?.present()}
            >
              <IonLabel>
                {t("common:settings.organization.current.label", {
                  name: activeOrganization?.name ?? "",
                })}
              </IonLabel>
            </IonItem>
          )}
          <IonItem detail detailIcon={chevronRightIcon} routerLink={Routes.PersonalInformation()}>
            <IonLabel>{t("routes:personal-information.title")}</IonLabel>
          </IonItem>
          {/* <IonItem detail detailIcon={chevronRightIcon} routerLink={Routes.CustomFields()}>
            <IonLabel>{t("routes:custom-fields.title")}</IonLabel>
          </IonItem> */}
          <IonItem detail detailIcon={chevronRightIcon} routerLink={Routes.Notifications()}>
            <IonLabel>{t("routes:notifications.title")}</IonLabel>
          </IonItem>
          <IonItem detail detailIcon={chevronRightIcon} routerLink={Routes.Contact()}>
            <IonLabel>{t("routes:contact.title")}</IonLabel>
          </IonItem>
          <IonItem detail detailIcon={chevronRightIcon} routerLink={Routes.Privacy()}>
            <IonLabel>{t("routes:privacy.title")}</IonLabel>
          </IonItem>
          <IonItem
            detail
            detailIcon={chevronRightIcon}
            href="https://admin.heyboomer.nl/terms-of-service"
          >
            <IonLabel>{t("routes:terms.title")}</IonLabel>
          </IonItem>
          <IonItem
            detail
            detailIcon={chevronRightIcon}
            button
            onClick={() => modalSignOut.current?.present()}
          >
            <IonLabel>{t("modals:sign-out.trigger")}</IonLabel>
          </IonItem>
          <IonItem
            className="text-xs text-gray-500"
            lines="none"
            onClick={async () => {
              await Haptics.impact({ style: ImpactStyle.Light });

              const newCount = versionClickCount + 1;
              setVersionClickCount(newCount);

              if (newCount >= 5) {
                const devInfo = {
                  version: packageJson.version,
                  mode: import.meta.env.MODE,
                  prod: import.meta.env.PROD,
                  dev: import.meta.env.DEV,
                  organizationSlug: import.meta.env.VITE_ORGANIZATION_SLUG,
                  backendUrl: import.meta.env.VITE_BACKEND_URL,
                  hostUrl: import.meta.env.VITE_HOST_URL,
                };

                await Haptics.impact({ style: ImpactStyle.Heavy });

                setDebugInfo(devInfo);
                modalDebugInfo.current?.present();
                setVersionClickCount(0);
              }
            }}
          >
            <IonLabel>Versie: {packageJson.version}</IonLabel>
          </IonItem>

          <ModalSheetAutoHeight
            ref={modalSignOut}
            onWillDismiss={async (event) => {
              if (event.detail.role === "confirm") {
                // Cleanup device registration before signing out
                await cleanupDeviceRegistration();
                await authClient.signOut();

                // IMPORTANT: Force full page reload instead of using router navigation
                // After sign-out, the nested IonRouterOutlet stack can become corrupted,
                // causing navigation issues (e.g., forgot password link not working).
                // A hard reload ensures the entire router state is properly reset.
                window.location.href = Routes.AuthSignInPassword();
              }
            }}
          >
            <div className="ion-padding flex flex-col gap-6 pt-6">
              <strong className="text-primary">{t("modals:sign-out.title")}</strong>

              <div className="flex flex-col gap-2">
                <Button onClick={() => modalSignOut.current?.dismiss(true, "confirm")}>
                  {t("modals:sign-out.confirm")}
                </Button>
                <Button
                  color="primary"
                  fill="outline"
                  onClick={() => modalSignOut.current?.dismiss()}
                >
                  {t("modals:sign-out.cancel")}
                </Button>
              </div>
            </div>
          </ModalSheetAutoHeight>

          <OrganizationSwitcher ref={modalOrganizationSwitcher} />

          <ModalSheetAutoHeight ref={modalDebugInfo}>
            <div className="ion-padding flex flex-col gap-6 pt-6">
              <strong className="text-primary">Development Info</strong>

              <div className="flex flex-col gap-2">
                {Object.entries(debugInfo).map(([key, value]) => (
                  <div key={key} className="flex flex-col gap-1">
                    <span className="text-xs font-semibold uppercase text-gray-500">{key}</span>
                    <span className="font-mono text-sm">{String(value)}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2)).then(() => {
                      toast.success("Copied to clipboard");
                    });
                  }}
                >
                  Copy to Clipboard
                </Button>
                <Button
                  color="primary"
                  fill="outline"
                  onClick={() => modalDebugInfo.current?.dismiss()}
                >
                  Close
                </Button>
              </div>
            </div>
          </ModalSheetAutoHeight>
        </IonList>
      </Content>
    </IonPage>
  );
}
